import { COMMANDER_PARITY_FIELD_MAPPINGS, getCommanderSectionCatalog } from "./data/commanderSectionCatalog";
import { CITY_DECORATION_PERMANENT_OPTIONS } from "./data/cityDecorationCatalog";
import { TEMPORARY_BUFF_CATALOG } from "./data/temporaryBuffCatalog";
import { getVipLevelOption } from "./data/vipCatalog";
import { getProsperityLevelOption } from "./data/prosperityCatalog";
import { getMilitaryRankOption } from "./data/militaryRankCatalog";
import { getAllianceBenefitBuildingOption } from "./data/allianceBenefitBuildingCatalog";
import {
  computeOfficerSkillPreview,
  decimalStarToStarStage,
  getOfficerCatalogEntryById,
  getOfficerEquipmentEffectContributions,
  getOfficerMasteryValue,
  isOfficerSkillSlotUnlocked,
  resolveUnworldlyTierBySkill,
} from "./data/officerCatalog";

export const PARITY_ARRAY_FIELDS = Object.freeze([
  "ba_tech",
  "ba_h_tech",
  "tow_sps",
  "titan_b_i",
  "fighter_b_i",
  "sw_items",
  "cw_items",
  "ew_items",
  "sw_sp_eff",
  "cw_sp_eff",
  "s_n_bfs",
  "c_n_bfs",
  "royal_it",
  "tk_i_b",
  "gvg_s",
]);

export const DEFAULT_COMMANDER_PARITY_PROFILE = Object.freeze({
  lvl: 0,
  t_b_r: 1,
  ba_tech: [],
  ba_h_tech: [],
  tow_sps: [],
  titan_b_i: [],
  fighter_b_i: [],
  sw_items: [],
  cw_items: [],
  ew_items: [],
  sw_sp_eff: [],
  cw_sp_eff: [],
  s_n_bfs: [],
  c_n_bfs: [],
  royal_it: [],
  tk_i_b: [],
  gvg_s: [],
});

const TITAN_TYPE_SOURCE_SPIRIT = 21;
const TITAN_TYPE_EMPRESS = 20;
const COMBAT_CONTEXT_ALL = "all";
const COMBAT_CONTEXT_INTERNAL = "internal";
const COMBAT_CONTEXT_CROSS_NATION = "crossNation";
const CROSS_NATION_ONLY_EFFECT_IDS = new Set([
  50462871, // Cross-Nation Battle Troops Expansion
  50462874, // Cross-Nation War Mobilization Troops Expansion
]);
const BIOCHEMICAL_GROWTH_REWARD_TECH_POINTS = 3;
// These gene unlocks are account-wide caps/one-off buffs, not per-pet repeats.
const BIOCHEMICAL_GENE_SINGLE_APPLY_EFFECT_IDS = new Set([
  50397429,
  50462851,
]);
const BIOCHEMICAL_GENE_SUPPRESSED_ROW_IDS = new Set([
  1011,
]);
const BIOCHEMICAL_GENE_EXTRA_SINGLE_APPLY_ROW_IDS = new Set([
  1013,
]);

const asFinite = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const EFFECT_TYPE_CATEGORY = 3;
const EFFECT_TYPE_PERCENT_SUBCATEGORY = 1;
const getGidCategory = (gid) => (Math.trunc(Number(gid || 0)) >>> 24);
const getGidSubCategory = (gid) => ((Math.trunc(Number(gid || 0)) >>> 16) & 0xff);
const isPercentEffectGid = (gid) => (
  getGidCategory(gid) === EFFECT_TYPE_CATEGORY
  && getGidSubCategory(gid) === EFFECT_TYPE_PERCENT_SUBCATEGORY
);

const formatParityEffectDisplayValue = (value, effectId = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (isPercentEffectGid(effectId)) {
    const percentValue = Math.round((numeric / 10) * 1000) / 1000;
    return `${Number.isInteger(percentValue) ? percentValue.toFixed(0) : percentValue.toFixed(3).replace(/\.?0+$/, "")}%`;
  }
  return Number.isInteger(numeric) ? String(numeric) : String(Math.round(numeric * 1000) / 1000);
};

const asBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "n", "off"].includes(normalized)) return false;
  return Boolean(fallback);
};

const normalizeId = (value) => {
  const parsed = Math.trunc(asFinite(value, NaN));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeIdLevelRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const deduped = new Map();
  rows.forEach((row) => {
    const id = normalizeId(row?.id);
    if (!id) return;
    const level = Math.max(0, Math.floor(asFinite(row?.level, 0)));
    if (level <= 0) return;
    const existing = deduped.get(id);
    if (!existing || level > existing.level) {
      deduped.set(id, { id, level });
    }
  });
  return Array.from(deduped.values());
};

const normalizeTitanSkinRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const deduped = new Map();
  rows.forEach((row) => {
    const id = normalizeId(row?.id);
    if (!id) return;
    const level = Math.max(0, Math.floor(asFinite(row?.level, 0)));
    const existing = deduped.get(id);
    if (!existing || level > existing.level) {
      deduped.set(id, { id, level });
    }
  });
  return Array.from(deduped.values());
};

const pairArrayToIdLevelRows = (rawArray) => {
  if (!Array.isArray(rawArray)) return [];
  const rows = [];
  for (let i = 0; i < rawArray.length - 1; i += 2) {
    const id = normalizeId(rawArray[i]);
    const level = Math.max(0, Math.floor(asFinite(rawArray[i + 1], 0)));
    if (!id || level <= 0) continue;
    rows.push({ id, level });
  }
  return normalizeIdLevelRows(rows);
};

const sumIdLevelRowsToPairArray = (rows) => {
  const totals = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = normalizeId(row?.id);
    const level = Math.round(asFinite(row?.level, 0));
    if (!id || level === 0) return;
    totals.set(id, Math.round(asFinite(totals.get(id), 0)) + level);
  });
  return Array.from(totals.entries())
    .map(([id, level]) => ({ id: Math.trunc(id), level: Math.round(asFinite(level, 0)) }))
    .filter((entry) => entry.id > 0 && entry.level !== 0)
    .sort((left, right) => left.id - right.id)
    .flatMap((entry) => [entry.id, entry.level]);
};

const parseSectionSourcePath = (sourcePath) => {
  const text = String(sourcePath || "").trim();
  if (!text || !text.includes(".")) return null;
  const [sectionKey, dataKey] = text.split(".");
  if (!sectionKey || !dataKey) return null;
  return { sectionKey, dataKey };
};

const getRowsForSectionSourcePath = (sections = {}, sourcePath = "") => {
  const parsed = parseSectionSourcePath(sourcePath);
  if (!parsed) return [];
  const sectionData = sections?.[parsed.sectionKey]?.data;
  if (!sectionData || typeof sectionData !== "object") return [];
  return normalizeIdLevelRows(sectionData?.[parsed.dataKey]);
};

const getCatalogRowsForSectionSourcePath = (sourcePath = "") => {
  const parsed = parseSectionSourcePath(sourcePath);
  if (!parsed) return [];
  return getSectionGroupRows(getCommanderSectionCatalog(parsed.sectionKey), parsed.dataKey);
};

const normalizeCombatContext = (value) => {
  if (value === COMBAT_CONTEXT_INTERNAL) return COMBAT_CONTEXT_INTERNAL;
  if (value === COMBAT_CONTEXT_CROSS_NATION) return COMBAT_CONTEXT_CROSS_NATION;
  return COMBAT_CONTEXT_ALL;
};

const getCombatScopeText = (...sources) => sources
  .map((source) => {
    if (!source || typeof source !== "object") return "";
    return [
      source.label,
      source.name,
      source.description,
      source.title,
      source.subtitle,
    ].filter(Boolean).join(" ");
  })
  .join(" ")
  .trim();

const getCrossNationMultiplier = (text = "") => {
  const normalized = String(text || "");
  const percentMatch = normalized.match(/effect\s+increases\s+by\s+([0-9]+(?:\.[0-9]+)?)%\s+for\s+cross[-\s]?nation/i);
  if (percentMatch) {
    return 1 + (Number(percentMatch[1]) / 100);
  }
  const timesMatch = normalized.match(/cross[-\s]?nation[^.]*?([0-9]+(?:\.[0-9]+)?)\s*times/i);
  if (timesMatch) {
    return Number(timesMatch[1]);
  }
  return 1;
};

const isCrossNationOnlyText = (text = "") => {
  const normalized = String(text || "");
  if (!/cross[-\s]?nation/i.test(normalized)) return false;
  if (/effect\s+increases\s+by\s+[0-9]+(?:\.[0-9]+)?%\s+for\s+cross[-\s]?nation/i.test(normalized)) {
    return false;
  }
  if (/by\s+[0-9]+(?:\.[0-9]+)?\s*times/i.test(normalized)) {
    return false;
  }
  return (
    /^cross[-\s]?nation\s+battle/i.test(normalized)
    || /when\s+in\s+cross[-\s]?nation/i.test(normalized)
    || /in\s+cross[-\s]?nation\s+battle/i.test(normalized)
    || /cross[-\s]?nation\s+combat/i.test(normalized)
  );
};

const getCombatScopedValue = (value, combatContext, ...scopeSources) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) return 0;
  const context = normalizeCombatContext(combatContext);
  if (context === COMBAT_CONTEXT_ALL) return numeric;
  const effectId = scopeSources.reduce((found, source) => found || normalizeId(source?.effectId), null);
  if (effectId && CROSS_NATION_ONLY_EFFECT_IDS.has(effectId)) {
    return context === COMBAT_CONTEXT_CROSS_NATION ? numeric : 0;
  }
  const scopeText = getCombatScopeText(...scopeSources);
  const crossNationOnly = isCrossNationOnlyText(scopeText);
  if (crossNationOnly && context !== COMBAT_CONTEXT_CROSS_NATION) return 0;
  if (context === COMBAT_CONTEXT_CROSS_NATION) {
    return numeric * getCrossNationMultiplier(scopeText);
  }
  return numeric;
};

const addEffectRowsToMap = (effectMap, effects = [], options = {}, ...scopeSources) => {
  if (!(effectMap instanceof Map)) return;
  const combatContext = normalizeCombatContext(options?.combatContext);
  (Array.isArray(effects) ? effects : []).forEach((effect) => {
    const effectId = normalizeId(effect?.effectId);
    const rawValue = asFinite(effect?.value, 0);
    const value = Math.round(getCombatScopedValue(rawValue, combatContext, ...scopeSources, effect));
    if (!effectId || value === 0) return;
    effectMap.set(effectId, Math.round(asFinite(effectMap.get(effectId), 0)) + value);
  });
};

const compileScopedEffectRows = (effects = [], options = {}, ...scopeSources) => {
  const rows = [];
  const combatContext = normalizeCombatContext(options?.combatContext);
  (Array.isArray(effects) ? effects : []).forEach((effect) => {
    const effectId = normalizeId(effect?.effectId);
    const rawValue = asFinite(effect?.value, 0);
    const value = Math.round(getCombatScopedValue(rawValue, combatContext, ...scopeSources, effect));
    if (!effectId || value === 0) return;
    rows.push({ effectId, value });
  });
  return rows;
};

const resolveLevelRow = (rows = [], selectedLevel = 0, levelKey = "level") => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (!sourceRows.length) return null;
  const target = Math.max(0, Math.floor(asFinite(selectedLevel, 0)));
  return sourceRows.find((row) => Math.floor(asFinite(row?.[levelKey], 0)) === target)
    || [...sourceRows].reverse().find((row) => Math.floor(asFinite(row?.[levelKey], 0)) <= target)
    || sourceRows[0];
};

const resolvePetLevelRow = (rows = [], petId = 0, selectedLevel = 0) => {
  const normalizedPetId = normalizeId(petId);
  if (!normalizedPetId) return null;
  return resolveLevelRow(
    (Array.isArray(rows) ? rows : [])
      .filter((row) => normalizeId(row?.petId) === normalizedPetId)
      .sort((left, right) => Math.floor(asFinite(left?.level, 0)) - Math.floor(asFinite(right?.level, 0))),
    selectedLevel,
  );
};

const getBiochemicalEarnedGenePoints = (biochemicalData = {}, petIntimacyRows = []) => (
  (Array.isArray(biochemicalData?.pets) ? biochemicalData.pets : []).reduce((sum, pet) => {
    const row = resolvePetLevelRow(
      petIntimacyRows,
      pet?.gid,
      pet?.intimacyLevel ?? pet?.incubationLevel,
    );
    const unclaimedGrowthRewardCount = Array.isArray(pet?.growthRewards)
      ? pet.growthRewards.filter((reward) => Math.floor(asFinite(reward?.status, 0)) < 1).length
      : 0;
    const unclaimedGrowthRewardPoints = unclaimedGrowthRewardCount * BIOCHEMICAL_GROWTH_REWARD_TECH_POINTS;
    return sum + Math.max(0, Math.floor(asFinite(row?.totalTechPoints, 0)) - unclaimedGrowthRewardPoints);
  }, 0)
);

const resolveBiochemicalGeneTechLevel = (biochemicalData = {}, geneRows = [], petIntimacyRows = []) => {
  const capturedGeneTechLevel = normalizeId(biochemicalData?.geneTechLevel) || 0;
  const earnedPoints = getBiochemicalEarnedGenePoints(biochemicalData, petIntimacyRows);
  const sortedGeneRows = (Array.isArray(geneRows) ? geneRows : [])
    .filter((row) => normalizeId(row?.id))
    .sort((left, right) => Math.floor(asFinite(left?.sortIndex, 0)) - Math.floor(asFinite(right?.sortIndex, 0)));
  if (earnedPoints > 0 && sortedGeneRows.length > 0) {
    const row = sortedGeneRows.find((entry) => Math.floor(asFinite(entry?.sortIndex, 0)) === earnedPoints)
      || [...sortedGeneRows].reverse().find((entry) => Math.floor(asFinite(entry?.sortIndex, 0)) <= earnedPoints)
      || null;
    return Math.max(capturedGeneTechLevel, normalizeId(row?.id) || 0);
  }
  return capturedGeneTechLevel;
};

const getBiochemicalGeneRowMultiplier = (row = {}, defaultMultiplier = 1) => {
  const rowId = normalizeId(row?.id);
  if (rowId && BIOCHEMICAL_GENE_SUPPRESSED_ROW_IDS.has(rowId)) return 0;
  if (row?.skill) return 1;
  const effects = Array.isArray(row?.effects) ? row.effects : [];
  if (effects.some((effect) => BIOCHEMICAL_GENE_SINGLE_APPLY_EFFECT_IDS.has(normalizeId(effect?.effectId)))) {
    return 1;
  }
  const baseMultiplier = Math.max(1, Math.floor(asFinite(defaultMultiplier, 1)));
  return baseMultiplier + (rowId && BIOCHEMICAL_GENE_EXTRA_SINGLE_APPLY_ROW_IDS.has(rowId) ? 1 : 0);
};

const formatBiochemicalPetDisplayLevel = (level = 0) => (
  Math.max(1, Math.floor(asFinite(level, 0)) + 1)
);

const resolveCumulativeLevelEffects = (rows = [], selectedLevel = 0, levelKey = "level") => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (!sourceRows.length) return { row: null, effects: [] };
  const target = Math.max(0, Math.floor(asFinite(selectedLevel, 0)));
  const sortedRows = [...sourceRows].sort((left, right) => (
    Math.floor(asFinite(left?.[levelKey], 0)) - Math.floor(asFinite(right?.[levelKey], 0))
  ));
  const activeRows = sortedRows.filter((row) => Math.floor(asFinite(row?.[levelKey], 0)) <= target);
  const rowsToApply = activeRows.length ? activeRows : [sortedRows[0]];
  const effectsById = new Map();
  rowsToApply.forEach((row) => {
    (Array.isArray(row?.effects) ? row.effects : []).forEach((effect) => {
      const effectId = normalizeId(effect?.effectId);
      if (!effectId) return;
      effectsById.set(effectId, {
        ...effect,
        effectId,
        value: asFinite(effect?.value, 0),
      });
    });
  });
  return {
    row: rowsToApply[rowsToApply.length - 1] || null,
    effects: Array.from(effectsById.values()),
  };
};

const resolveQualityRow = (rows = [], selectedRank = 0) => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  if (!sourceRows.length) return null;
  const target = Math.max(0, Math.floor(asFinite(selectedRank, 0)));
  return sourceRows.find((row) => Math.floor(asFinite(row?.rank, 0)) === target)
    || [...sourceRows].reverse().find((row) => Math.floor(asFinite(row?.rank, 0)) <= target)
    || sourceRows[0];
};

const mapRowsById = (rows = [], idKeys = ["id"]) => new Map(
  (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const id = idKeys.reduce((found, key) => found || normalizeId(row?.[key]), null);
      return [id, row];
    })
    .filter(([id]) => Boolean(id)),
);

const getCatalogGroupMap = (sectionKey, groupKey, idKeys = ["id"]) => (
  mapRowsById(getSectionGroupRows(getCommanderSectionCatalog(sectionKey), groupKey), idKeys)
);

const formatContributionLabel = (...sources) => {
  const found = sources.find((source) => {
    if (!source || typeof source !== "object") return false;
    return String(source.label || source.name || source.title || "").trim();
  });
  if (found) return String(found.label || found.name || found.title || "").trim();
  const idSource = sources.find((source) => source && typeof source === "object" && normalizeId(source.id ?? source.gid ?? source.typeId));
  const id = normalizeId(idSource?.id ?? idSource?.gid ?? idSource?.typeId);
  return id ? `Entry ${id}` : "Profile entry";
};

const ACTIVE_COMMANDER_SKILL_BUFF_IDS = new Set([
  83886364, // Fast Expansion is manually activated, not a permanent commander stat.
]);

const formatContributionLevel = (entry = {}, selectedRow = null, options = {}) => {
  const parts = [];
  const hasDisplayLevel = Number.isFinite(Number(entry?.displayLevel ?? selectedRow?.displayLevel));
  const levelOffset = hasDisplayLevel ? 0 : Math.floor(asFinite(options?.levelOffset, 0));
  const level = Number(entry?.displayLevel ?? selectedRow?.displayLevel ?? entry?.level ?? selectedRow?.level);
  if (Number.isFinite(level) && level > 0) parts.push(`Level ${Math.floor(level)}`);
  else if (Number.isFinite(level) && level === 0 && levelOffset > 0) parts.push(`Level ${levelOffset}`);
  if (Number.isFinite(level) && level > 0 && levelOffset !== 0) {
    parts[parts.length - 1] = `Level ${Math.floor(level + levelOffset)}`;
  }
  const rank = Number(entry?.quality ?? entry?.rank ?? selectedRow?.rank);
  if (Number.isFinite(rank) && rank > 0) parts.push(`Quality ${Math.floor(rank)}`);
  const star = Number(entry?.star ?? selectedRow?.star);
  const stage = Number(entry?.stage ?? selectedRow?.stage);
  if (Number.isFinite(star) && star > 0) {
    parts.push(Number.isFinite(stage) && stage > 0 ? `Star ${Math.floor(star)}.${Math.floor(stage)}` : `Star ${Math.floor(star)}`);
  }
  const slot = Number(entry?.slot);
  const index = Number(entry?.index);
  if (Number.isFinite(slot) && slot > 0) parts.push(`Slot ${Math.floor(slot)}`);
  if (Number.isFinite(index) && index > 0) parts.push(`Component ${Math.floor(index)}`);
  return parts.join(" | ");
};

const pushContributionDetail = (details, {
  sectionKey,
  groupLabel,
  label,
  meta = "",
  effects = [],
}) => {
  const normalizedEffects = (Array.isArray(effects) ? effects : [])
    .map((effect) => ({
      effectId: normalizeId(effect?.effectId),
      value: Math.round(asFinite(effect?.value, 0)),
    }))
    .filter((effect) => effect.effectId && effect.value !== 0);
  if (!sectionKey || !normalizedEffects.length) return;
  details.push({
    key: `${sectionKey}:${groupLabel || "group"}:${label || "entry"}:${details.length}`,
    sectionKey,
    groupLabel: groupLabel || "Profile",
    label: label || "Profile entry",
    meta,
    effects: normalizedEffects,
  });
};

const addSelectedLevelRowEffects = (effectMap, option, selectedLevel, options = {}) => {
  if (!option) return;
  const levelRow = resolveLevelRow(option?.levelRows, selectedLevel);
  addEffectRowsToMap(effectMap, levelRow?.effects, options, option, levelRow);
};

const addCumulativeLevelRowEffects = (effectMap, option, selectedLevel, options = {}) => {
  if (!option) return;
  const result = resolveCumulativeLevelEffects(option?.levelRows, selectedLevel);
  addEffectRowsToMap(effectMap, result.effects, options, option, result.row);
};

const addSelectedQualityEffects = (effectMap, option, selectedRank, options = {}) => {
  if (!option) return;
  const qualityRow = resolveQualityRow(option?.qualityRows, selectedRank);
  addEffectRowsToMap(effectMap, qualityRow?.effects || option?.firstRankEffects, options, option, qualityRow);
};

const addPerLevelEffect = (effectMap, option, selectedInternalLevel, selectedKnown, options = {}) => {
  const effectId = normalizeId(option?.effectId);
  const perLevel = asFinite(option?.effectValue, 0);
  const displayLevel = selectedKnown ? Math.max(1, Math.floor(asFinite(selectedInternalLevel, 0)) + 1) : 0;
  if (!effectId || perLevel === 0 || displayLevel <= 0) return;
  const value = Math.round(getCombatScopedValue(perLevel * displayLevel, options?.combatContext, option));
  if (value === 0) return;
  effectMap.set(effectId, Math.round(asFinite(effectMap.get(effectId), 0)) + value);
};

const resolveAntiqueStarStageRow = (collectionRow = null, selected = {}) => {
  const rows = Array.isArray(collectionRow?.starRows) ? collectionRow.starRows : [];
  if (!rows.length) return null;
  const targetStar = Math.max(0, Math.floor(asFinite(selected?.star ?? selected?.level, 0)));
  const targetStage = Math.max(0, Math.floor(asFinite(selected?.stage, 0)));
  return rows.find((row) => (
    Math.floor(asFinite(row?.star, 0)) === targetStar
    && Math.floor(asFinite(row?.stage, 0)) === targetStage
  ))
    || [...rows].reverse().find((row) => (
      Math.floor(asFinite(row?.star, 0)) < targetStar
      || (
        Math.floor(asFinite(row?.star, 0)) === targetStar
        && Math.floor(asFinite(row?.stage, 0)) <= targetStage
      )
    ))
    || rows[0];
};

const resolveAntiqueSuitRow = (suit = null, selectedAntiqueStarsById = new Map()) => {
  const collectionIds = Array.isArray(suit?.collectionIds)
    ? suit.collectionIds.map(normalizeId).filter(Boolean)
    : [];
  if (!collectionIds.length) {
    return {
      collectionIds,
      row: null,
      requiredStar: 0,
      matchedCount: 0,
    };
  }
  const suitRows = Array.isArray(suit?.levelRows) ? suit.levelRows : [];
  const row = [...suitRows].reverse().find((candidate) => {
    const requiredStar = Math.floor(asFinite(candidate?.star, 0));
    if (requiredStar <= 0) return false;
    return collectionIds.every((collectionId) => (
      Math.max(0, Math.floor(asFinite(selectedAntiqueStarsById.get(collectionId), 0))) >= requiredStar
    ));
  }) || null;
  const requiredStar = row ? Math.floor(asFinite(row?.star, 0)) : 0;
  const matchedCount = requiredStar > 0
    ? collectionIds.filter((collectionId) => (
      Math.max(0, Math.floor(asFinite(selectedAntiqueStarsById.get(collectionId), 0))) >= requiredStar
    )).length
    : 0;
  return {
    collectionIds,
    row,
    requiredStar,
    matchedCount,
  };
};

let islandDecorationSetDefinitionsCache = null;

const getIslandDecorationSetDefinitions = () => {
  if (islandDecorationSetDefinitionsCache) return islandDecorationSetDefinitionsCache;
  const rows = getSectionGroupRows(getCommanderSectionCatalog("island"), "cityDecorationOptions");
  const rowsById = mapRowsById(rows);
  const groupsBySkinGid = new Map();
  rows.forEach((row) => {
    const skinGid = normalizeId(row?.skinGid);
    if (!skinGid) return;
    if (!groupsBySkinGid.has(skinGid)) groupsBySkinGid.set(skinGid, []);
    groupsBySkinGid.get(skinGid).push(row);
  });
  islandDecorationSetDefinitionsCache = Array.from(groupsBySkinGid.entries())
    .map(([skinGid, groupRows]) => {
      if (!Array.isArray(groupRows) || groupRows.length <= 1) return null;
      const linkedMainId = groupRows.reduce((found, row) => found || normalizeId(row?.skinBuffGid), null);
      const main = rowsById.get(linkedMainId)
        || groupRows.find((row) => !normalizeId(row?.skinBuffGid))
        || groupRows[0];
      const mainId = normalizeId(main?.id);
      const memberIds = groupRows.map((row) => normalizeId(row?.id)).filter(Boolean);
      if (!mainId || memberIds.length <= 1) return null;
      return {
        skinGid,
        main,
        mainId,
        memberRows: groupRows,
        memberIds,
      };
    })
    .filter(Boolean);
  return islandDecorationSetDefinitionsCache;
};

const buildSelectedRowsById = (rows = [], idKeys = ["id"]) => {
  const selectedById = new Map();
  (Array.isArray(rows) ? rows : []).forEach((entry) => {
    const id = idKeys.reduce((found, key) => found || normalizeId(entry?.[key]), null);
    if (!id) return;
    const level = Math.max(0, Math.floor(asFinite(entry?.level, 0)));
    const existing = selectedById.get(id);
    if (!existing || level > existing.level) {
      selectedById.set(id, { ...entry, id, level });
    }
  });
  return selectedById;
};

const resolveCompletedIslandDecorationSets = (sections = {}) => {
  const selectedById = buildSelectedRowsById(sections?.island?.data?.cityDecorationOptions);
  if (!selectedById.size) return [];
  return getIslandDecorationSetDefinitions()
    .map((definition) => {
      const hasCompleteSet = definition.memberIds.every((memberId) => selectedById.has(memberId));
      if (!hasCompleteSet) return null;
      const selectedMain = selectedById.get(definition.mainId);
      if (!selectedMain) return null;
      const result = resolveCumulativeLevelEffects(definition.main?.setLevelRows, selectedMain.level);
      if (!result.effects.length) return null;
      return {
        ...definition,
        selectedMain,
        selectedRow: result.row,
        effects: result.effects,
      };
    })
    .filter(Boolean);
};

const compileSectionCatalogEffectRows = (sections = {}, options = {}) => {
  const effectMap = new Map();
  const addSelectedRows = ({
    sectionKey,
    dataKey,
    groupKey,
    idKeys = ["id"],
    mode = "levelRow",
    catalogSectionKey = sectionKey,
  }) => {
    const selectedRows = Array.isArray(sections?.[sectionKey]?.data?.[dataKey])
      ? sections[sectionKey].data[dataKey]
      : [];
    if (!selectedRows.length) return;
    const catalogMap = getCatalogGroupMap(catalogSectionKey, groupKey, idKeys);
    selectedRows.forEach((entry) => {
      const id = idKeys.reduce((found, key) => found || normalizeId(entry?.[key]), null);
      const option = catalogMap.get(id);
      if (!option) return;
      if (mode === "perLevel") {
        addPerLevelEffect(effectMap, option, entry?.level, true, options);
      } else if (mode === "quality") {
        addSelectedQualityEffects(effectMap, option, entry?.level, options);
      } else if (mode === "cumulativeLevelRows") {
        addCumulativeLevelRowEffects(effectMap, option, entry?.level, options);
      } else {
        addSelectedLevelRowEffects(effectMap, option, entry?.level, options);
      }
    });
  };

  addSelectedRows({ sectionKey: "academy", dataKey: "academyTech", groupKey: "academyTechOptions", mode: "perLevel" });
  addSelectedRows({ sectionKey: "academy", dataKey: "vehicleTech", groupKey: "vehicleTechOptions", mode: "perLevel" });
  addSelectedRows({ sectionKey: "alliance", dataKey: "allianceTech", groupKey: "allianceTechOptions", mode: "perLevel", catalogSectionKey: "academy" });

  const userTechOptionMap = getCatalogGroupMap("academy", "userTechOptions");
  const selectedUserTechById = new Map();
  (Array.isArray(sections?.academy?.data?.userTech) ? sections.academy.data.userTech : [])
    .forEach((entry) => {
      const id = normalizeId(entry?.id);
      if (!id) return;
      selectedUserTechById.set(id, Math.max(
        Math.floor(asFinite(selectedUserTechById.get(id), 0)),
        Math.floor(asFinite(entry?.level, 0)),
      ));
    });
  ["developmentSkills", "warSkills"].forEach((dataKey) => {
    (Array.isArray(sections?.commanderSkills?.data?.[dataKey]) ? sections.commanderSkills.data[dataKey] : [])
      .forEach((entry) => {
        const id = normalizeId(entry?.id);
        if (!id) return;
        selectedUserTechById.set(id, Math.max(
          Math.floor(asFinite(selectedUserTechById.get(id), 0)),
          Math.floor(asFinite(entry?.level, 0)),
        ));
      });
  });
  selectedUserTechById.forEach((level, id) => {
    addPerLevelEffect(effectMap, userTechOptionMap.get(id), level, true, options);
  });

  const vipData = sections?.commanderBoosts?.data?.vip && typeof sections.commanderBoosts.data.vip === "object"
    ? sections.commanderBoosts.data.vip
    : {};
  if (asBoolean(vipData?.active, false)) {
    addEffectRowsToMap(effectMap, getVipLevelOption(vipData?.level)?.effects, options);
  }

  const biochemicalData = sections?.biochemicalWorkshop?.data || {};
  const biochemicalCatalog = getCommanderSectionCatalog("biochemicalWorkshop");
  const geneRows = getSectionGroupRows(biochemicalCatalog, "geneOptions");
  const petIntimacyRows = getSectionGroupRows(biochemicalCatalog, "petIntimacyLevelOptions");
  const petHealRows = getSectionGroupRows(biochemicalCatalog, "petHealLevelOptions");
  (Array.isArray(biochemicalData?.pets) ? biochemicalData.pets : []).forEach((pet) => {
    addEffectRowsToMap(
      effectMap,
      resolvePetLevelRow(petIntimacyRows, pet?.gid, pet?.intimacyLevel ?? pet?.incubationLevel)?.effects,
      options,
    );
    addEffectRowsToMap(
      effectMap,
      resolvePetLevelRow(petHealRows, pet?.gid, pet?.healLevel ?? pet?.hatcheryLevel)?.effects,
      options,
    );
  });
  const geneTechLevel = resolveBiochemicalGeneTechLevel(biochemicalData, geneRows, petIntimacyRows);
  const biochemicalPetGeneMultiplier = Math.max(
    1,
    (Array.isArray(biochemicalData?.pets) ? biochemicalData.pets : [])
      .filter((pet) => Array.isArray(pet?.collectedGids) && pet.collectedGids.length > 0)
      .length,
  );
  geneRows
    .filter((row) => normalizeId(row?.id) && normalizeId(row?.id) <= geneTechLevel)
    .forEach((row) => {
      const multiplier = getBiochemicalGeneRowMultiplier(row, biochemicalPetGeneMultiplier);
      for (let index = 0; index < multiplier; index += 1) {
        addEffectRowsToMap(effectMap, row?.effects, options, row);
      }
    });

  const cityBuildingMap = getCatalogGroupMap("city", "buildingOptions");
  (Array.isArray(sections?.city?.data?.buildings) ? sections.city.data.buildings : [])
    .forEach((entry) => {
      const option = cityBuildingMap.get(normalizeId(entry?.gid ?? entry?.id));
      addSelectedLevelRowEffects(effectMap, option, entry?.level, options);
    });

  const equipmentData = sections?.equipment?.data || {};
  const equipmentMap = getCatalogGroupMap("equipment", "equipOptions");
  const selectedBaseBySlot = new Map();
  (Array.isArray(equipmentData?.equipOptions) ? equipmentData.equipOptions : []).forEach((entry) => {
    const option = equipmentMap.get(normalizeId(entry?.id));
    if (!option) return;
    const slot = Math.max(0, Math.floor(asFinite(option?.slot, 0)));
    if (slot > 0) selectedBaseBySlot.set(slot, option);
    addSelectedQualityEffects(effectMap, option, entry?.level, options);
  });
  const extensionTypeById = new Map(
    getSectionGroupRows(getCommanderSectionCatalog("equipment"), "extensionTypeOptions")
      .map((row) => [normalizeId(row?.typeId), row])
      .filter(([id]) => Boolean(id)),
  );
  (Array.isArray(equipmentData?.extensionSlots) ? equipmentData.extensionSlots : []).forEach((entry) => {
    const slot = Math.max(0, Math.floor(asFinite(entry?.slot, 0)));
    const index = Math.max(0, Math.floor(asFinite(entry?.index, 0)));
    const selectedBase = selectedBaseBySlot.get(slot);
    const peripheralTypes = Array.isArray(selectedBase?.peripheralTypes) ? selectedBase.peripheralTypes : [];
    const typeRow = extensionTypeById.get(normalizeId(peripheralTypes[index - 1]));
    const rankRows = Array.isArray(typeRow?.rankRows) ? typeRow.rankRows : [];
    const rankRow = resolveQualityRow(rankRows, entry?.quality);
    const levelRow = resolveLevelRow(rankRow?.levelRows, entry?.level);
    addEffectRowsToMap(effectMap, levelRow?.effects, options, typeRow, rankRow, levelRow);
  });

  const antiqueMap = getCatalogGroupMap("antiques", "collectionOptions");
  const selectedAntiqueStarsById = new Map();
  (Array.isArray(sections?.antiques?.data?.collectionOptions) ? sections.antiques.data.collectionOptions : [])
    .forEach((entry) => {
      const collectionId = normalizeId(entry?.id);
      if (collectionId) {
        selectedAntiqueStarsById.set(collectionId, Math.max(0, Math.floor(asFinite(entry?.star ?? entry?.level, 0))));
      }
      const option = antiqueMap.get(collectionId);
      const starRow = resolveAntiqueStarStageRow(option, entry);
      addEffectRowsToMap(effectMap, starRow?.effects, options, option, starRow);
    });
  getSectionGroupRows(getCommanderSectionCatalog("antiques"), "collectionSuitOptions")
    .forEach((suit) => {
      const suitResolution = resolveAntiqueSuitRow(suit, selectedAntiqueStarsById);
      addEffectRowsToMap(effectMap, suitResolution.row?.effects, options, suit, suitResolution.row);
    });

  addSelectedRows({ sectionKey: "island", dataKey: "cityDecorationOptions", groupKey: "cityDecorationOptions", mode: "cumulativeLevelRows" });
  resolveCompletedIslandDecorationSets(sections).forEach((setResolution) => {
    addEffectRowsToMap(effectMap, setResolution.effects, options, setResolution.main, setResolution.selectedRow);
  });
  addSelectedRows({ sectionKey: "island", dataKey: "dockDecorationOptions", groupKey: "dockDecorationOptions", mode: "cumulativeLevelRows" });
  (Array.isArray(sections?.alliance?.data?.benefitBuildings) ? sections.alliance.data.benefitBuildings : [])
    .forEach((entry) => {
      const option = getAllianceBenefitBuildingOption(entry?.id ?? entry?.gid ?? entry?.type);
      const count = Math.max(0, Math.floor(asFinite(entry?.count ?? entry?.level ?? 0, 0)));
      if (!option || count <= 0) return;
      const effects = (Array.isArray(option.effects) ? option.effects : [])
        .map((effect) => ({
          effectId: effect.effectId,
          value: Math.round(asFinite(effect.value, 0) * count),
        }));
      addEffectRowsToMap(effectMap, effects, options, option);
    });
  const prosperityData = sections?.commanderBoosts?.data?.prosperity && typeof sections.commanderBoosts.data.prosperity === "object"
    ? sections.commanderBoosts.data.prosperity
    : {};
  if (asBoolean(prosperityData?.active, false)) {
    addEffectRowsToMap(effectMap, getProsperityLevelOption(prosperityData?.level)?.effects, options);
  }

  const militaryRankData = sections?.commanderBoosts?.data?.militaryRank && typeof sections.commanderBoosts.data.militaryRank === "object"
    ? sections.commanderBoosts.data.militaryRank
    : {};
  addEffectRowsToMap(effectMap, getMilitaryRankOption(militaryRankData?.level)?.effects, options);

  (Array.isArray(sections?.cityDecorations?.data?.permanent) ? sections.cityDecorations.data.permanent : [])
    .forEach((entry) => {
      const option = CITY_DECORATION_PERMANENT_BY_ID.get(normalizeId(entry?.id ?? entry?.itemGid));
      const levelRow = resolveLevelRow(option?.levels, entry?.level);
      addEffectRowsToMap(effectMap, levelRow?.effects, options, option, levelRow);
    });

  (Array.isArray(sections?.temporaryBuffs?.data?.active) ? sections.temporaryBuffs.data.active : [])
    .forEach((entry) => {
      const buffId = String(entry?.buffId || `${entry?.sourceType || "item"}:${entry?.itemGid || entry?.id}`).trim();
      const option = TEMPORARY_BUFF_BY_BUFF_ID.get(buffId);
      const durationRow = resolveLevelRow(option?.durationOptions, entry?.level);
      addEffectRowsToMap(
        effectMap,
        durationRow?.effects?.length ? durationRow.effects : option?.effects,
        options,
        option,
        durationRow,
      );
    });

  const warplaneData = sections?.warplane?.data || {};
  const warplaneLevelRows = getSectionGroupRows(getCommanderSectionCatalog("warplane"), "levelRows");
  const selectedWarplaneGid = normalizeId(warplaneData?.selectedGid);
  const selectedWarplaneLevelRow = warplaneLevelRows.find((row) => normalizeId(row?.gid) === selectedWarplaneGid)
    || resolveLevelRow(warplaneLevelRows, warplaneData?.level);
  addEffectRowsToMap(effectMap, selectedWarplaneLevelRow?.baseEffects, options, selectedWarplaneLevelRow);
  addSelectedRows({ sectionKey: "warplane", dataKey: "skills", groupKey: "skillOptions" });
  addSelectedRows({ sectionKey: "warplane", dataKey: "equipSkills", groupKey: "equipSkillOptions" });
  addSelectedRows({ sectionKey: "warplane", dataKey: "equips", groupKey: "equipOptions" });
  (Array.isArray(warplaneData?.skins) ? warplaneData.skins : []).forEach((entry) => {
    const option = getCatalogGroupMap("warplane", "skinOptions").get(normalizeId(entry?.id ?? entry?.gid));
    addSelectedLevelRowEffects(effectMap, option, entry?.level, options);
  });

  const titansCatalog = getCommanderSectionCatalog("titans");
  const titanCatalogIndex = buildTitanCatalogIndex(titansCatalog);
  const normalizedTitans = normalizeTitansSectionData(sections?.titans?.data || {}, titansCatalog);
  TITAN_SIDE_KEYS.forEach((sideKey) => {
    const side = normalizedTitans?.[sideKey] || normalizeTitanSideData({});
    const sideCatalog = titanCatalogIndex?.perType?.[sideKey] || {};
    side.spells.forEach((entry) => addSelectedLevelRowEffects(effectMap, sideCatalog?.spellMap?.get(normalizeId(entry?.id)), entry?.level, options));
    side.equipSkills.forEach((entry) => addSelectedLevelRowEffects(effectMap, sideCatalog?.equipSkillMap?.get(normalizeId(entry?.id)), entry?.level, options));
  });

  return Array.from(effectMap.entries())
    .map(([id, level]) => ({ id: Math.trunc(id), level: Math.round(asFinite(level, 0)) }))
    .filter((row) => row.id > 0 && row.level !== 0);
};

const compileDerivedRowsForParityField = (fieldKey, sections = {}, options = {}) => {
  const mapping = COMMANDER_PARITY_FIELD_MAPPINGS?.[fieldKey];
  const resolvedRows = Array.isArray(mapping?.resolvedIds) ? mapping.resolvedIds : [];
  if (!resolvedRows.length) return [];
  const combatContext = normalizeCombatContext(options?.combatContext);

  const selectedBySourcePath = new Map();
  const catalogRowsBySourcePath = new Map();
  resolvedRows.forEach((row) => {
    const sources = Array.isArray(row?.sources) ? row.sources : [];
    sources.forEach((sourcePath) => {
      if (!parseSectionSourcePath(sourcePath)) return;
      if (!selectedBySourcePath.has(sourcePath)) {
        const sourceRows = getRowsForSectionSourcePath(sections, sourcePath);
        selectedBySourcePath.set(
          sourcePath,
          new Map(sourceRows.map((entry) => [entry.id, entry.level])),
        );
      }
      if (!catalogRowsBySourcePath.has(sourcePath)) {
        catalogRowsBySourcePath.set(
          sourcePath,
          new Map(
            getCatalogRowsForSectionSourcePath(sourcePath)
              .map((entry) => [normalizeId(entry?.id), entry])
              .filter(([id]) => Boolean(id)),
          ),
        );
      }
    });
  });

  const deduped = new Map();
  resolvedRows.forEach((row) => {
    const id = normalizeId(row?.id);
    if (!id) return;
    const sources = Array.isArray(row?.sources) ? row.sources : [];
    sources.forEach((sourcePath) => {
      const selectedMap = selectedBySourcePath.get(sourcePath);
      if (!selectedMap) return;
      const catalogRow = catalogRowsBySourcePath.get(sourcePath)?.get(id) || null;
      const level = Math.max(0, Math.round(getCombatScopedValue(
        asFinite(selectedMap.get(id), 0),
        combatContext,
        catalogRow,
        row,
      )));
      if (level <= 0) return;
      const existing = Math.round(asFinite(deduped.get(id)?.level, 0));
      deduped.set(id, { id, level: existing + level });
    });
  });

  return Array.from(deduped.values());
};

const normalizeNumberArray = (rawArray) => {
  if (!Array.isArray(rawArray)) return [];
  return rawArray
    .map((entry) => asFinite(entry, NaN))
    .filter((entry) => Number.isFinite(entry))
    .map((entry) => Math.trunc(entry));
};

const normalizeBaseParityProfile = (rawProfile = {}) => {
  const source = rawProfile && typeof rawProfile === "object" ? rawProfile : {};
  const normalized = {
    lvl: Math.max(0, Math.floor(asFinite(source.lvl, DEFAULT_COMMANDER_PARITY_PROFILE.lvl))),
    t_b_r: Math.max(0.1, asFinite(source.t_b_r, DEFAULT_COMMANDER_PARITY_PROFILE.t_b_r)),
  };
  PARITY_ARRAY_FIELDS.forEach((field) => {
    normalized[field] = normalizeNumberArray(source[field]);
  });
  return normalized;
};

const getSectionGroupRows = (sectionCatalog, groupKey) => {
  const groups = Array.isArray(sectionCatalog?.groups) ? sectionCatalog.groups : [];
  const group = groups.find((entry) => String(entry?.key || "") === String(groupKey));
  return Array.isArray(group?.rows) ? group.rows : [];
};

const TITAN_SIDE_META = Object.freeze({
  sourceSpirit: { typeId: TITAN_TYPE_SOURCE_SPIRIT },
  empress: { typeId: TITAN_TYPE_EMPRESS },
});
const TITAN_SIDE_KEYS = Object.freeze(Object.keys(TITAN_SIDE_META));

const normalizeTitanSideData = (raw = {}) => {
  const skins = normalizeTitanSkinRows(raw?.skins);
  if (!skins.length) {
    const skinGid = normalizeId(raw?.skinGid);
    if (skinGid) {
      skins.push({
        id: skinGid,
        level: Math.max(0, Math.floor(asFinite(raw?.skinStarLevel, 0))),
      });
    }
  }
  const skinStarLevel = skins.reduce(
    (sum, row) => sum + Math.max(0, Math.floor(asFinite(row?.level, 0))),
    0,
  );
  return {
    level: Math.max(0, Math.floor(asFinite(raw?.level, 0))),
    evolutionStage: Math.max(0, Math.floor(asFinite(raw?.evolutionStage, 0))),
    evolutionLevel: Math.max(0, Math.floor(asFinite(raw?.evolutionLevel, 0))),
    skinGid: skins.length ? normalizeId(skins[0]?.id) : normalizeId(raw?.skinGid),
    skinStarLevel,
    skins,
    spells: normalizeIdLevelRows(raw?.spells),
    equipPieces: normalizeIdLevelRows(raw?.equipPieces),
    equipSkills: normalizeIdLevelRows(raw?.equipSkills),
  };
};

const normalizeTitansSectionData = (raw = {}, titansCatalog = null) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const hasTypedSides = (
    (source?.sourceSpirit && typeof source.sourceSpirit === "object")
    || (source?.empress && typeof source.empress === "object")
  );
  if (hasTypedSides) {
    return {
      sourceSpirit: normalizeTitanSideData(source?.sourceSpirit || {}),
      empress: normalizeTitanSideData(source?.empress || {}),
    };
  }

  const catalogIndex = buildTitanCatalogIndex(titansCatalog || getCommanderSectionCatalog("titans"));
  const splitRowsBySide = (rows = [], sourceMap = new Map(), empressMap = new Map()) => {
    const out = { sourceSpirit: [], empress: [] };
    normalizeIdLevelRows(rows).forEach((entry) => {
      const id = normalizeId(entry?.id);
      const level = Math.max(0, Math.floor(asFinite(entry?.level, 0)));
      if (!id || level <= 0) return;
      const sourceHas = sourceMap instanceof Map ? sourceMap.has(id) : false;
      const empressHas = empressMap instanceof Map ? empressMap.has(id) : false;
      if (sourceHas && !empressHas) {
        out.sourceSpirit.push({ id, level });
      } else if (empressHas && !sourceHas) {
        out.empress.push({ id, level });
      } else {
        out.sourceSpirit.push({ id, level });
      }
    });
    return out;
  };
  const splitSkinsBySide = (rows = []) => {
    const out = {
      sourceSpirit: { skinGid: null, skinStarLevel: 0, skins: [] },
      empress: { skinGid: null, skinStarLevel: 0, skins: [] },
    };
    normalizeTitanSkinRows(rows).forEach((entry, index) => {
      const id = normalizeId(entry?.id);
      const level = Math.max(0, Math.floor(asFinite(entry?.level, 0)));
      if (!id || level < 0) return;
      const sourceHas = catalogIndex?.perType?.sourceSpirit?.skinMap instanceof Map
        ? catalogIndex.perType.sourceSpirit.skinMap.has(id)
        : false;
      const empressHas = catalogIndex?.perType?.empress?.skinMap instanceof Map
        ? catalogIndex.perType.empress.skinMap.has(id)
        : false;
      let sideKey = "sourceSpirit";
      if (sourceHas && !empressHas) {
        sideKey = "sourceSpirit";
      } else if (empressHas && !sourceHas) {
        sideKey = "empress";
      } else if (index === 1) {
        sideKey = "empress";
      }
      const sideSkinMap = sideKey === "sourceSpirit"
        ? catalogIndex?.perType?.sourceSpirit?.skinMap
        : catalogIndex?.perType?.empress?.skinMap;
      const option = sideSkinMap instanceof Map ? sideSkinMap.get(id) : null;
      const maxLevel = Math.max(0, Math.floor(asFinite(option?.maxStarLevel, 0)));
      const clampedLevel = maxLevel > 0 ? Math.min(level, maxLevel) : level;
      out[sideKey].skins.push({ id, level: clampedLevel });
    });
    out.sourceSpirit.skins = normalizeTitanSkinRows(out.sourceSpirit.skins);
    out.empress.skins = normalizeTitanSkinRows(out.empress.skins);
    out.sourceSpirit.skinStarLevel = out.sourceSpirit.skins.reduce(
      (sum, row) => sum + Math.max(0, Math.floor(asFinite(row?.level, 0))),
      0,
    );
    out.empress.skinStarLevel = out.empress.skins.reduce(
      (sum, row) => sum + Math.max(0, Math.floor(asFinite(row?.level, 0))),
      0,
    );
    out.sourceSpirit.skinGid = out.sourceSpirit.skins.length ? out.sourceSpirit.skins[0].id : null;
    out.empress.skinGid = out.empress.skins.length ? out.empress.skins[0].id : null;
    return out;
  };

  const splitSpells = splitRowsBySide(
    source?.spells,
    catalogIndex?.perType?.sourceSpirit?.spellMap || new Map(),
    catalogIndex?.perType?.empress?.spellMap || new Map(),
  );
  const splitPieces = splitRowsBySide(
    source?.equipPieces,
    catalogIndex?.perType?.sourceSpirit?.equipPieceMap || new Map(),
    catalogIndex?.perType?.empress?.equipPieceMap || new Map(),
  );
  const splitSkills = splitRowsBySide(
    source?.equipSkills,
    catalogIndex?.perType?.sourceSpirit?.equipSkillMap || new Map(),
    catalogIndex?.perType?.empress?.equipSkillMap || new Map(),
  );
  const splitSkins = splitSkinsBySide(source?.skins);

  // Legacy fallback: split into source spirit / empress using catalog type ownership.
  return {
    sourceSpirit: normalizeTitanSideData({
      level: source?.sourceSpiritLevel,
      evolutionStage: 0,
      evolutionLevel: 0,
      spells: splitSpells.sourceSpirit,
      equipPieces: splitPieces.sourceSpirit,
      equipSkills: splitSkills.sourceSpirit,
      skinGid: splitSkins.sourceSpirit.skinGid,
      skinStarLevel: splitSkins.sourceSpirit.skinStarLevel,
      skins: splitSkins.sourceSpirit.skins,
    }),
    empress: normalizeTitanSideData({
      level: source?.empressLevel,
      evolutionStage: 0,
      evolutionLevel: 0,
      spells: splitSpells.empress,
      equipPieces: splitPieces.empress,
      equipSkills: splitSkills.empress,
      skinGid: splitSkins.empress.skinGid,
      skinStarLevel: splitSkins.empress.skinStarLevel,
      skins: splitSkins.empress.skins,
    }),
  };
};

const buildTitanCatalogIndex = (titansCatalog = null) => {
  const catalog = titansCatalog || getCommanderSectionCatalog("titans");
  const typeRows = getSectionGroupRows(catalog, "titanTypes");
  const spellRows = getSectionGroupRows(catalog, "spellOptions");
  const equipPieceRows = getSectionGroupRows(catalog, "equipPieceOptions");
  const equipSkillRows = getSectionGroupRows(catalog, "equipSkillOptions");
  const skinRows = getSectionGroupRows(catalog, "skinOptions");
  const skinStarRows = getSectionGroupRows(catalog, "skinStarMilestones");

  const toMap = (rows = []) => new Map(
    rows
      .map((row) => [normalizeId(row?.id), row])
      .filter(([id]) => Boolean(id)),
  );

  const typeRowByTypeId = new Map(
    typeRows
      .map((row) => [Number(row?.id || 0), row])
      .filter(([typeId]) => Number.isFinite(typeId) && typeId > 0),
  );

  const perType = {};
  TITAN_SIDE_KEYS.forEach((sideKey) => {
    const typeId = TITAN_SIDE_META[sideKey].typeId;
    const typeRow = typeRowByTypeId.get(typeId) || {};
    const spells = spellRows.filter((row) => {
      const rowTypes = Array.isArray(row?.titanTypes)
        ? row.titanTypes.map((value) => Number(value || 0)).filter(Number.isFinite)
        : [];
      return !rowTypes.length || rowTypes.includes(typeId);
    });
    const pieces = equipPieceRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const skills = equipSkillRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const skins = skinRows.filter((row) => Number(row?.titanType || 0) === typeId);
    const starRow = skinStarRows.find((row) => Number(row?.titanType || 0) === typeId) || null;
    perType[sideKey] = {
      typeId,
      typeRow,
      maxLevel: Math.max(0, Math.floor(asFinite(typeRow?.maxLevel, 0))),
      spellMap: toMap(spells),
      equipPieceMap: toMap(pieces),
      equipSkillMap: toMap(skills),
      skinMap: toMap(skins),
      skinStarRow: starRow,
    };
  });
  return { perType };
};

const resolveTitanLevelRow = (option = null, selectedLevel = 0) => {
  const rows = Array.isArray(option?.levelRows) ? option.levelRows : [];
  if (!rows.length) return null;
  const target = Math.max(0, Math.floor(asFinite(selectedLevel, 0)));
  return rows.find((row) => Math.floor(asFinite(row?.level, 0)) === target)
    || [...rows].reverse().find((row) => Math.floor(asFinite(row?.level, 0)) <= target)
    || rows[0];
};

const compileTitanBattleInfoFromSections = (titansData = {}, titansCatalog = null) => {
  const normalized = normalizeTitansSectionData(titansData, titansCatalog);
  const encoded = [];
  TITAN_SIDE_KEYS.forEach((sideKey) => {
    const sideData = normalized?.[sideKey] || normalizeTitanSideData({});
    const typeId = TITAN_SIDE_META[sideKey].typeId;
    const merged = new Map();
    [...sideData.spells, ...sideData.equipSkills].forEach((entry) => {
      const id = normalizeId(entry?.id);
      const level = Math.max(0, Math.floor(asFinite(entry?.level, 0)));
      if (!id || level <= 0) return;
      const existing = Math.max(0, Math.floor(asFinite(merged.get(id), 0)));
      if (level > existing) merged.set(id, level);
    });
    const includeGroup = merged.size > 0 || sideData.level > 0;
    if (!includeGroup) return;
    encoded.push(typeId);
    Array.from(merged.entries())
      .sort((left, right) => left[0] - right[0])
      .forEach(([id, level]) => encoded.push(Math.trunc(id), Math.trunc(level)));
    encoded.push(-100);
  });
  return encoded;
};

const compileTitanPassiveEffectRows = (titansData = {}, titansCatalog = null) => {
  const normalized = normalizeTitansSectionData(titansData, titansCatalog);
  const catalogIndex = buildTitanCatalogIndex(titansCatalog || getCommanderSectionCatalog("titans"));
  const effectMap = new Map();
  const addEffects = (effects = []) => {
    (Array.isArray(effects) ? effects : []).forEach((row) => {
      const effectId = normalizeId(row?.effectId);
      const value = Math.round(asFinite(row?.value, 0));
      if (!effectId || value === 0) return;
      const existing = Math.round(asFinite(effectMap.get(effectId), 0));
      effectMap.set(effectId, existing + value);
    });
  };
  const addProgressionEffects = (advancementRow = null, selectedStep = 0) => {
    const effectIds = Array.isArray(advancementRow?.effectIds)
      ? advancementRow.effectIds.map(normalizeId).filter(Boolean)
      : [];
    const effectValueRows = advancementRow?.effectValueRows && typeof advancementRow.effectValueRows === "object"
      ? advancementRow.effectValueRows
      : {};
    effectIds.forEach((effectId, effectIndex) => {
      const values = Array.isArray(effectValueRows[`effectValue${effectIndex + 1}`])
        ? effectValueRows[`effectValue${effectIndex + 1}`]
        : [];
      if (!values.length) return;
      const index = Math.max(0, Math.min(values.length - 1, Math.floor(asFinite(selectedStep, 0))));
      const value = Math.round(asFinite(values[index], 0));
      if (value === 0) return;
      const existing = Math.round(asFinite(effectMap.get(effectId), 0));
      effectMap.set(effectId, existing + value);
    });
  };

  TITAN_SIDE_KEYS.forEach((sideKey) => {
    const sideData = normalized?.[sideKey] || normalizeTitanSideData({});
    const sideCatalog = catalogIndex?.perType?.[sideKey] || {};
    const typeAdvancementRows = Array.isArray(sideCatalog?.typeRow?.advancementRows)
      ? sideCatalog.typeRow.advancementRows
      : [];
    const advancementRow = typeAdvancementRows.find((row) => Math.floor(asFinite(row?.stage, 0)) === Math.floor(asFinite(sideData?.evolutionStage, 0)))
      || [...typeAdvancementRows].reverse().find((row) => Math.floor(asFinite(row?.stage, 0)) <= Math.floor(asFinite(sideData?.evolutionStage, 0)));
    addProgressionEffects(advancementRow, sideData?.evolutionLevel);
    sideData.equipPieces.forEach((entry) => {
      const option = sideCatalog?.equipPieceMap?.get(normalizeId(entry?.id));
      const levelRow = resolveTitanLevelRow(option, entry?.level || 0);
      addEffects(levelRow?.effects || []);
    });
    const skinRows = Array.isArray(sideData?.skins) ? sideData.skins : [];
    skinRows.forEach((entry) => {
      const option = sideCatalog?.skinMap?.get(normalizeId(entry?.id));
      const skinRow = resolveTitanLevelRow(option, entry?.level || 0);
      addEffects(skinRow?.effects || []);
    });
    const milestones = Array.isArray(sideCatalog?.skinStarRow?.milestones) ? sideCatalog.skinStarRow.milestones : [];
    milestones
      .filter((row) => Math.floor(asFinite(row?.starLevel, 0)) <= Math.floor(asFinite(sideData?.skinStarLevel, 0)))
      .forEach((row) => addEffects(row?.effects || []));
  });

  return Array.from(effectMap.entries())
    .map(([id, level]) => ({ id: Math.trunc(id), level: Math.round(asFinite(level, 0)) }))
    .filter((row) => row.id > 0 && row.level !== 0);
};

const getSectionCatalogGroupRows = (sectionCatalog = {}, groupKey = "") => {
  const groups = Array.isArray(sectionCatalog?.groups) ? sectionCatalog.groups : [];
  const group = groups.find((entry) => String(entry?.key || "") === String(groupKey || ""));
  return Array.isArray(group?.rows) ? group.rows : [];
};

const CITY_DECORATION_PERMANENT_BY_ID = new Map(
  (Array.isArray(CITY_DECORATION_PERMANENT_OPTIONS) ? CITY_DECORATION_PERMANENT_OPTIONS : [])
    .map((row) => [normalizeId(row?.id || row?.decorationId || row?.itemGid), row])
    .filter(([id]) => Boolean(id)),
);

const TEMPORARY_BUFF_BY_BUFF_ID = new Map(
  (Array.isArray(TEMPORARY_BUFF_CATALOG) ? TEMPORARY_BUFF_CATALOG : [])
    .map((row) => [String(row?.buffId || `${row?.sourceType || "item"}:${row?.itemGid || row?.id}`).trim(), row])
    .filter(([buffId]) => Boolean(buffId)),
);

const compileBadgePassiveEffectRows = (badgesData = {}, badgesCatalog = null) => {
  const catalogRows = getSectionCatalogGroupRows(badgesCatalog || getCommanderSectionCatalog("badges"), "badgeOptions");
  if (!catalogRows.length) return [];
  const selectedByGid = new Map(
    (Array.isArray(badgesData?.equipped) ? badgesData.equipped : [])
      .map((row) => [normalizeId(row?.gid || row?.id), Math.max(0, Math.floor(asFinite(row?.level, 0)))])
      .filter(([gid]) => gid > 0),
  );
  const effectMap = new Map();
  catalogRows.forEach((badge) => {
    const gid = normalizeId(badge?.gid || badge?.id);
    const selectedLevel = selectedByGid.get(gid);
    if (!gid || selectedLevel === undefined) return;
    const levelRows = Array.isArray(badge?.levelRows) ? badge.levelRows : [];
    const selectedLevelRow = levelRows.find((row) => Math.floor(asFinite(row?.level, 0)) === selectedLevel)
      || [...levelRows].reverse().find((row) => Math.floor(asFinite(row?.level, 0)) <= selectedLevel);
    (Array.isArray(selectedLevelRow?.effects) ? selectedLevelRow.effects : []).forEach((effect) => {
      const effectId = normalizeId(effect?.effectId);
      const value = Math.round(asFinite(effect?.value, 0));
      if (!effectId || value === 0) return;
      effectMap.set(effectId, Math.round(asFinite(effectMap.get(effectId), 0)) + value);
    });
  });
  return Array.from(effectMap.entries())
    .map(([id, level]) => ({ id: Math.trunc(id), level: Math.round(asFinite(level, 0)) }))
    .filter((row) => row.id > 0 && row.level !== 0);
};

const mergeCommanderSkillRowsById = (sections = {}) => {
  const selectedById = new Map();
  const addRows = (rows = []) => {
    (Array.isArray(rows) ? rows : []).forEach((entry) => {
      const id = normalizeId(entry?.id);
      if (!id || ACTIVE_COMMANDER_SKILL_BUFF_IDS.has(id)) return;
      selectedById.set(id, Math.max(
        Math.floor(asFinite(selectedById.get(id), 0)),
        Math.floor(asFinite(entry?.level, 0)),
      ));
    });
  };
  addRows(sections?.academy?.data?.userTech);
  addRows(sections?.commanderSkills?.data?.developmentSkills);
  addRows(sections?.commanderSkills?.data?.warSkills);
  return selectedById;
};

export const buildSectionContributionDetails = (sections = {}, options = {}) => {
  const details = [];
  const addSelectedRows = ({
    sectionKey,
    dataKey,
    groupKey,
    groupLabel,
    idKeys = ["id"],
    mode = "levelRow",
    catalogSectionKey = sectionKey,
  }) => {
    const selectedRows = Array.isArray(sections?.[sectionKey]?.data?.[dataKey])
      ? sections[sectionKey].data[dataKey]
      : [];
    if (!selectedRows.length) return;
    const catalogMap = getCatalogGroupMap(catalogSectionKey, groupKey, idKeys);
    selectedRows.forEach((entry) => {
      const id = idKeys.reduce((found, key) => found || normalizeId(entry?.[key]), null);
      const option = catalogMap.get(id);
      if (!option) return;
      let effects = [];
      let selectedRow = null;
      if (mode === "perLevel") {
        const effectId = normalizeId(option?.effectId);
        const perLevel = asFinite(option?.effectValue, 0);
        const displayLevel = Math.max(1, Math.floor(asFinite(entry?.level, 0)) + 1);
        const value = Math.round(getCombatScopedValue(perLevel * displayLevel, options?.combatContext, option));
        selectedRow = { displayLevel };
        effects = effectId && value !== 0 ? [{ effectId, value }] : [];
      } else if (mode === "quality") {
        selectedRow = resolveQualityRow(option?.qualityRows, entry?.level);
        effects = compileScopedEffectRows(selectedRow?.effects || option?.firstRankEffects, options, option, selectedRow);
      } else if (mode === "cumulativeLevelRows") {
        const result = resolveCumulativeLevelEffects(option?.levelRows, entry?.level);
        selectedRow = result.row;
        effects = compileScopedEffectRows(result.effects, options, option, selectedRow);
      } else {
        selectedRow = resolveLevelRow(option?.levelRows, entry?.level);
        effects = compileScopedEffectRows(selectedRow?.effects, options, option, selectedRow);
      }
      pushContributionDetail(details, {
        sectionKey,
        groupLabel: groupLabel || groupKey,
        label: formatContributionLabel(option, entry),
        meta: formatContributionLevel(entry, selectedRow),
        effects,
      });
    });
  };

  addSelectedRows({ sectionKey: "academy", dataKey: "academyTech", groupKey: "academyTechOptions", groupLabel: "Academy Tech", mode: "perLevel" });
  addSelectedRows({ sectionKey: "academy", dataKey: "vehicleTech", groupKey: "vehicleTechOptions", groupLabel: "Vehicle Tech", mode: "perLevel" });
  addSelectedRows({ sectionKey: "alliance", dataKey: "allianceTech", groupKey: "allianceTechOptions", groupLabel: "Alliance Tech", mode: "perLevel", catalogSectionKey: "academy" });
  const commanderSkillCatalogMap = getCatalogGroupMap("academy", "userTechOptions");
  mergeCommanderSkillRowsById(sections).forEach((level, id) => {
    const option = commanderSkillCatalogMap.get(id);
    if (!option) return;
    const effectId = normalizeId(option?.effectId);
    const perLevel = asFinite(option?.effectValue, 0);
    const displayLevel = Math.max(1, Math.floor(asFinite(level, 0)) + 1);
    const value = Math.round(getCombatScopedValue(perLevel * displayLevel, options?.combatContext, option));
    pushContributionDetail(details, {
      sectionKey: "commanderSkills",
      groupLabel: Number(option?.category || 0) === 1 ? "Development Skills" : "War Skills",
      label: formatContributionLabel(option, { id }),
      meta: `Level ${displayLevel}`,
      effects: effectId && value !== 0 ? [{ effectId, value }] : [],
    });
  });
  (Array.isArray(sections?.alliance?.data?.benefitBuildings) ? sections.alliance.data.benefitBuildings : [])
    .forEach((entry) => {
      const option = getAllianceBenefitBuildingOption(entry?.id ?? entry?.gid ?? entry?.type);
      const count = Math.max(0, Math.floor(asFinite(entry?.count ?? entry?.level ?? 0, 0)));
      if (!option || count <= 0) return;
      pushContributionDetail(details, {
        sectionKey: "alliance",
        groupLabel: "Benefit Buildings",
        label: option.label || `Benefit Building #${option.id}`,
        meta: `${Number(count).toLocaleString()} captured`,
        effects: compileScopedEffectRows(
          (Array.isArray(option.effects) ? option.effects : []).map((effect) => ({
            effectId: effect.effectId,
            value: Math.round(asFinite(effect.value, 0) * count),
          })),
          options,
          option,
        ),
      });
    });

  const officersData = sections?.officers?.data && typeof sections.officers.data === "object"
    ? sections.officers.data
    : {};
  const officerDerived = compileOfficerRowsToParity(officersData?.rows, {
    combatContext: normalizeCombatContext(options?.combatContext),
  });
  (Array.isArray(officerDerived?.rows) ? officerDerived.rows : []).forEach((row) => {
    pushContributionDetail(details, {
      sectionKey: "officers",
      groupLabel: "Officers",
      label: String(row?.officerName || (row?.officerId ? `Officer ${row.officerId}` : "Officer")),
      meta: [
        row?.mastery ? `Mastery ${Math.round(asFinite(row.mastery, 0))}` : "",
      ].filter(Boolean).join(" | "),
      effects: row?.effects,
    });
  });

  const vipData = sections?.commanderBoosts?.data?.vip && typeof sections.commanderBoosts.data.vip === "object"
    ? sections.commanderBoosts.data.vip
    : {};
  if (asBoolean(vipData?.active, false)) {
    const vipRow = getVipLevelOption(vipData?.level);
    pushContributionDetail(details, {
      sectionKey: "commanderBoosts",
      groupLabel: "VIP",
      label: "VIP Privileges",
      meta: `Level ${Math.max(0, Math.floor(asFinite(vipData?.level, 0)))}`,
      effects: compileScopedEffectRows(vipRow?.effects, options, vipRow),
    });
  }

  const biochemicalData = sections?.biochemicalWorkshop?.data || {};
  const biochemicalCatalog = getCommanderSectionCatalog("biochemicalWorkshop");
  const geneRows = getSectionGroupRows(biochemicalCatalog, "geneOptions");
  const petIntimacyRows = getSectionGroupRows(biochemicalCatalog, "petIntimacyLevelOptions");
  const petHealRows = getSectionGroupRows(biochemicalCatalog, "petHealLevelOptions");
  (Array.isArray(biochemicalData?.pets) ? biochemicalData.pets : []).forEach((pet) => {
    const petLabel = String(pet?.name || "").trim()
      || resolvePetLevelRow(petIntimacyRows, pet?.gid, pet?.intimacyLevel ?? pet?.incubationLevel)?.petLabel
      || resolvePetLevelRow(petHealRows, pet?.gid, pet?.healLevel ?? pet?.hatcheryLevel)?.petLabel
      || `Pet ${pet?.gid || ""}`.trim();
    const intimacyRow = resolvePetLevelRow(petIntimacyRows, pet?.gid, pet?.intimacyLevel ?? pet?.incubationLevel);
    const healRow = resolvePetLevelRow(petHealRows, pet?.gid, pet?.healLevel ?? pet?.hatcheryLevel);
    if (intimacyRow) {
      pushContributionDetail(details, {
        sectionKey: "biochemicalWorkshop",
        groupLabel: "Biochemical Pet Intimacy",
        label: petLabel,
        meta: `Level ${formatBiochemicalPetDisplayLevel(pet?.intimacyLevel ?? pet?.incubationLevel)}`,
        effects: compileScopedEffectRows(intimacyRow?.effects, options, intimacyRow),
      });
    }
    if (healRow) {
      pushContributionDetail(details, {
        sectionKey: "biochemicalWorkshop",
        groupLabel: "Biochemical Pet Heal",
        label: petLabel,
        meta: `Level ${formatBiochemicalPetDisplayLevel(pet?.healLevel ?? pet?.hatcheryLevel)}`,
        effects: compileScopedEffectRows(healRow?.effects, options, healRow),
      });
    }
  });
  const geneTechLevel = resolveBiochemicalGeneTechLevel(biochemicalData, geneRows, petIntimacyRows);
  const biochemicalPetGeneMultiplier = Math.max(
    1,
    (Array.isArray(biochemicalData?.pets) ? biochemicalData.pets : [])
      .filter((pet) => Array.isArray(pet?.collectedGids) && pet.collectedGids.length > 0)
      .length,
  );
  geneRows
    .filter((row) => normalizeId(row?.id) && normalizeId(row?.id) <= geneTechLevel)
    .forEach((row) => {
      const multiplier = getBiochemicalGeneRowMultiplier(row, biochemicalPetGeneMultiplier);
      const effects = [];
      for (let index = 0; index < multiplier; index += 1) {
        effects.push(...compileScopedEffectRows(row?.effects, options, row));
      }
      pushContributionDetail(details, {
        sectionKey: "biochemicalWorkshop",
        groupLabel: "Biochemical Gene",
        label: formatContributionLabel(row),
        meta: multiplier > 1 ? `Applied ${multiplier} times` : "",
        effects,
      });
    });

  const cityBuildingMap = getCatalogGroupMap("city", "buildingOptions");
  (Array.isArray(sections?.city?.data?.buildings) ? sections.city.data.buildings : []).forEach((entry) => {
    const option = cityBuildingMap.get(normalizeId(entry?.gid ?? entry?.id));
    const levelRow = resolveLevelRow(option?.levelRows, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "city",
      groupLabel: "Buildings",
      label: formatContributionLabel(option, entry),
      meta: formatContributionLevel(entry, levelRow, { levelOffset: 1 }),
      effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
    });
  });

  const equipmentData = sections?.equipment?.data || {};
  const equipmentMap = getCatalogGroupMap("equipment", "equipOptions");
  const selectedBaseBySlot = new Map();
  (Array.isArray(equipmentData?.equipOptions) ? equipmentData.equipOptions : []).forEach((entry) => {
    const option = equipmentMap.get(normalizeId(entry?.id));
    if (!option) return;
    const slot = Math.max(0, Math.floor(asFinite(option?.slot, 0)));
    if (slot > 0) selectedBaseBySlot.set(slot, option);
    const rankRow = resolveQualityRow(option?.qualityRows, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "equipment",
      groupLabel: "Commander Equipment",
      label: formatContributionLabel(option, entry),
      meta: formatContributionLevel(entry, rankRow),
      effects: compileScopedEffectRows(rankRow?.effects || option?.firstRankEffects, options, option, rankRow),
    });
  });
  const extensionTypeById = new Map(
    getSectionGroupRows(getCommanderSectionCatalog("equipment"), "extensionTypeOptions")
      .map((row) => [normalizeId(row?.typeId), row])
      .filter(([id]) => Boolean(id)),
  );
  (Array.isArray(equipmentData?.extensionSlots) ? equipmentData.extensionSlots : []).forEach((entry) => {
    const slot = Math.max(0, Math.floor(asFinite(entry?.slot, 0)));
    const index = Math.max(0, Math.floor(asFinite(entry?.index, 0)));
    const selectedBase = selectedBaseBySlot.get(slot);
    const peripheralTypes = Array.isArray(selectedBase?.peripheralTypes) ? selectedBase.peripheralTypes : [];
    const typeRow = extensionTypeById.get(normalizeId(peripheralTypes[index - 1]));
    const rankRow = resolveQualityRow(typeRow?.rankRows, entry?.quality);
    const levelRow = resolveLevelRow(rankRow?.levelRows, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "equipment",
      groupLabel: "Extension Components",
      label: formatContributionLabel(typeRow, entry),
      meta: formatContributionLevel(entry, levelRow),
      effects: compileScopedEffectRows(levelRow?.effects, options, typeRow, rankRow, levelRow),
    });
  });

  const antiqueMap = getCatalogGroupMap("antiques", "collectionOptions");
  const selectedAntiqueStarsById = new Map();
  (Array.isArray(sections?.antiques?.data?.collectionOptions) ? sections.antiques.data.collectionOptions : []).forEach((entry) => {
    const collectionId = normalizeId(entry?.id);
    if (collectionId) selectedAntiqueStarsById.set(collectionId, Math.max(0, Math.floor(asFinite(entry?.star ?? entry?.level, 0))));
    const option = antiqueMap.get(collectionId);
    const starRow = resolveAntiqueStarStageRow(option, entry);
    pushContributionDetail(details, {
      sectionKey: "antiques",
      groupLabel: "Antiques",
      label: formatContributionLabel(option, entry),
      meta: formatContributionLevel(entry, starRow),
      effects: compileScopedEffectRows(starRow?.effects, options, option, starRow),
    });
  });
  getSectionGroupRows(getCommanderSectionCatalog("antiques"), "collectionSuitOptions").forEach((suit) => {
    const suitResolution = resolveAntiqueSuitRow(suit, selectedAntiqueStarsById);
    if (!suitResolution.row) return;
    pushContributionDetail(details, {
      sectionKey: "antiques",
      groupLabel: "Antique Sets",
      label: formatContributionLabel(suit),
      meta: `All ${suitResolution.collectionIds.length} antiques >= ${suitResolution.requiredStar} stars`,
      effects: compileScopedEffectRows(suitResolution.row?.effects, options, suit, suitResolution.row),
    });
  });

  addSelectedRows({ sectionKey: "island", dataKey: "cityDecorationOptions", groupKey: "cityDecorationOptions", groupLabel: "City Island", mode: "cumulativeLevelRows" });
  resolveCompletedIslandDecorationSets(sections).forEach((setResolution) => {
    pushContributionDetail(details, {
      sectionKey: "island",
      groupLabel: "City Island Sets",
      label: formatContributionLabel(setResolution.main),
      meta: [
        `Complete set (${setResolution.memberIds.length} pieces)`,
        formatContributionLevel(
          { ...setResolution.selectedMain, displayLevel: Math.floor(asFinite(setResolution.selectedMain?.level, 0)) + 1 },
          setResolution.selectedRow,
        ),
      ].filter(Boolean).join(" | "),
      effects: compileScopedEffectRows(setResolution.effects, options, setResolution.main, setResolution.selectedRow),
    });
  });
  addSelectedRows({ sectionKey: "island", dataKey: "dockDecorationOptions", groupKey: "dockDecorationOptions", groupLabel: "Island Dock", mode: "cumulativeLevelRows" });
  const prosperityData = sections?.commanderBoosts?.data?.prosperity && typeof sections.commanderBoosts.data.prosperity === "object"
    ? sections.commanderBoosts.data.prosperity
    : {};
  if (asBoolean(prosperityData?.active, false)) {
    const prosperityRow = getProsperityLevelOption(prosperityData?.level);
    pushContributionDetail(details, {
      sectionKey: "commanderBoosts",
      groupLabel: "Prosperity",
      label: "Prosperity",
      meta: `Level ${Math.max(0, Math.floor(asFinite(prosperityData?.level, 0)))}`,
      effects: compileScopedEffectRows(prosperityRow?.effects, options, prosperityRow),
    });
  }

  const militaryRankData = sections?.commanderBoosts?.data?.militaryRank && typeof sections.commanderBoosts.data.militaryRank === "object"
    ? sections.commanderBoosts.data.militaryRank
    : {};
  const militaryRankRow = getMilitaryRankOption(militaryRankData?.level);
  if (Number(militaryRankRow?.level || 0) > 0) {
    pushContributionDetail(details, {
      sectionKey: "commanderBoosts",
      groupLabel: "Military Rank",
      label: militaryRankRow?.label || "Military Rank",
      meta: `Level ${Math.max(0, Math.floor(asFinite(militaryRankData?.level, 0)))}`,
      effects: compileScopedEffectRows(militaryRankRow?.effects, options, militaryRankRow),
    });
  }

  (Array.isArray(sections?.cityDecorations?.data?.permanent) ? sections.cityDecorations.data.permanent : []).forEach((entry) => {
    const option = CITY_DECORATION_PERMANENT_BY_ID.get(normalizeId(entry?.id ?? entry?.itemGid));
    const levelRow = resolveLevelRow(option?.levels, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "cityDecorations",
      groupLabel: option?.categoryLabel || "City Decorations",
      label: formatContributionLabel(option, entry),
      meta: formatContributionLevel(entry, levelRow),
      effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
    });
  });

  (Array.isArray(sections?.temporaryBuffs?.data?.active) ? sections.temporaryBuffs.data.active : []).forEach((entry) => {
    const buffId = String(entry?.buffId || `${entry?.sourceType || "item"}:${entry?.itemGid || entry?.id}`).trim();
    const option = TEMPORARY_BUFF_BY_BUFF_ID.get(buffId);
    const durationRow = resolveLevelRow(option?.durationOptions, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "temporaryBuffs",
      groupLabel: option?.categoryLabel || "Temporary Buffs",
      label: formatContributionLabel(option, entry),
      meta: [
        formatContributionLevel(entry, durationRow),
        String(durationRow?.durationLabel || "").trim(),
      ].filter(Boolean).join(" | "),
      effects: compileScopedEffectRows(
        durationRow?.effects?.length ? durationRow.effects : option?.effects,
        options,
        option,
        durationRow,
      ),
    });
  });

  const warplaneData = sections?.warplane?.data || {};
  const warplaneLevelRows = getSectionGroupRows(getCommanderSectionCatalog("warplane"), "levelRows");
  const selectedWarplaneGid = normalizeId(warplaneData?.selectedGid);
  const selectedWarplaneLevelRow = warplaneLevelRows.find((row) => normalizeId(row?.gid) === selectedWarplaneGid)
    || resolveLevelRow(warplaneLevelRows, warplaneData?.level);
  pushContributionDetail(details, {
    sectionKey: "warplane",
    groupLabel: "Warplane Level",
    label: formatContributionLabel(selectedWarplaneLevelRow, { label: warplaneData?.name || "Warplane" }),
    meta: formatContributionLevel(warplaneData, selectedWarplaneLevelRow),
    effects: compileScopedEffectRows(selectedWarplaneLevelRow?.baseEffects, options, selectedWarplaneLevelRow),
  });
  addSelectedRows({ sectionKey: "warplane", dataKey: "skills", groupKey: "skillOptions", groupLabel: "Lab Skills" });
  addSelectedRows({ sectionKey: "warplane", dataKey: "equipSkills", groupKey: "equipSkillOptions", groupLabel: "Equipment Skills" });
  addSelectedRows({ sectionKey: "warplane", dataKey: "equips", groupKey: "equipOptions", groupLabel: "Equipment" });
  (Array.isArray(warplaneData?.skins) ? warplaneData.skins : []).forEach((entry) => {
    const option = getCatalogGroupMap("warplane", "skinOptions").get(normalizeId(entry?.id ?? entry?.gid));
    const levelRow = resolveLevelRow(option?.levelRows, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "warplane",
      groupLabel: "Skins",
      label: formatContributionLabel(option, entry),
      meta: formatContributionLevel(entry, levelRow),
      effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
    });
  });

  const titansCatalog = getCommanderSectionCatalog("titans");
  const titanCatalogIndex = buildTitanCatalogIndex(titansCatalog);
  const normalizedTitans = normalizeTitansSectionData(sections?.titans?.data || {}, titansCatalog);
  TITAN_SIDE_KEYS.forEach((sideKey) => {
    const side = normalizedTitans?.[sideKey] || normalizeTitanSideData({});
    const sideCatalog = titanCatalogIndex?.perType?.[sideKey] || {};
    const titanLabel = sideKey === "sourceSpirit" ? "Source Spirit" : "Empress";
    const advancementRows = Array.isArray(sideCatalog?.typeRow?.advancementRows) ? sideCatalog.typeRow.advancementRows : [];
    const advancementRow = advancementRows.find((row) => Math.floor(asFinite(row?.stage, 0)) === Math.floor(asFinite(side?.evolutionStage, 0)))
      || [...advancementRows].reverse().find((row) => Math.floor(asFinite(row?.stage, 0)) <= Math.floor(asFinite(side?.evolutionStage, 0)));
    if (advancementRow) {
      const effects = [];
      const effectIds = Array.isArray(advancementRow?.effectIds) ? advancementRow.effectIds.map(normalizeId).filter(Boolean) : [];
      const effectValueRows = advancementRow?.effectValueRows && typeof advancementRow.effectValueRows === "object" ? advancementRow.effectValueRows : {};
      effectIds.forEach((effectId, effectIndex) => {
        const values = Array.isArray(effectValueRows[`effectValue${effectIndex + 1}`]) ? effectValueRows[`effectValue${effectIndex + 1}`] : [];
        const idx = Math.max(0, Math.min(values.length - 1, Math.floor(asFinite(side?.evolutionLevel, 0))));
        const value = Math.round(asFinite(values[idx], 0));
        if (effectId && value !== 0) effects.push({ effectId, value });
      });
      pushContributionDetail(details, {
        sectionKey: "titans",
        groupLabel: "Evolution",
        label: `${titanLabel} Evolution`,
        meta: `Tier ${Math.floor(asFinite(side?.evolutionStage, 0))} | Step ${Math.floor(asFinite(side?.evolutionLevel, 0))}`,
        effects,
      });
    }
    side.spells.forEach((entry) => {
      const option = sideCatalog?.spellMap?.get(normalizeId(entry?.id));
      const levelRow = resolveLevelRow(option?.levelRows, entry?.level);
      pushContributionDetail(details, {
        sectionKey: "titans",
        groupLabel: `${titanLabel} Talents`,
        label: formatContributionLabel(option, entry),
        meta: formatContributionLevel(entry, levelRow),
        effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
      });
    });
    side.equipPieces.forEach((entry) => {
      const option = sideCatalog?.equipPieceMap?.get(normalizeId(entry?.id));
      const levelRow = resolveTitanLevelRow(option, entry?.level || 0);
      pushContributionDetail(details, {
        sectionKey: "titans",
        groupLabel: `${titanLabel} Equipment`,
        label: formatContributionLabel(option, entry),
        meta: formatContributionLevel(entry, levelRow),
        effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
      });
    });
    side.equipSkills.forEach((entry) => {
      const option = sideCatalog?.equipSkillMap?.get(normalizeId(entry?.id));
      const levelRow = resolveLevelRow(option?.levelRows, entry?.level);
      pushContributionDetail(details, {
        sectionKey: "titans",
        groupLabel: `${titanLabel} Equipment Skills`,
        label: formatContributionLabel(option, entry),
        meta: formatContributionLevel(entry, levelRow),
        effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
      });
    });
    (Array.isArray(side?.skins) ? side.skins : []).forEach((entry) => {
      const option = sideCatalog?.skinMap?.get(normalizeId(entry?.id));
      const levelRow = resolveTitanLevelRow(option, entry?.level || 0);
      pushContributionDetail(details, {
        sectionKey: "titans",
        groupLabel: `${titanLabel} Skins`,
        label: formatContributionLabel(option, entry),
        meta: formatContributionLevel(entry, levelRow),
        effects: compileScopedEffectRows(levelRow?.effects, options, option, levelRow),
      });
    });
    const milestones = Array.isArray(sideCatalog?.skinStarRow?.milestones) ? sideCatalog.skinStarRow.milestones : [];
    milestones
      .filter((row) => Math.floor(asFinite(row?.starLevel, 0)) <= Math.floor(asFinite(side?.skinStarLevel, 0)))
      .forEach((row) => {
        pushContributionDetail(details, {
          sectionKey: "titans",
          groupLabel: `${titanLabel} Skin Stars`,
          label: `${titanLabel} Shared Skin Star ${Math.floor(asFinite(row?.starLevel, 0))}`,
          meta: `Total skin stars ${Math.floor(asFinite(side?.skinStarLevel, 0))}`,
          effects: compileScopedEffectRows(row?.effects, options, row),
        });
      });
  });

  const badgeCatalogRows = getSectionCatalogGroupRows(getCommanderSectionCatalog("badges"), "badgeOptions");
  const selectedBadgeByGid = new Map(
    (Array.isArray(sections?.badges?.data?.equipped) ? sections.badges.data.equipped : [])
      .map((row) => [normalizeId(row?.gid || row?.id), row])
      .filter(([gid]) => gid > 0),
  );
  badgeCatalogRows.forEach((badge) => {
    const gid = normalizeId(badge?.gid || badge?.id);
    const entry = selectedBadgeByGid.get(gid);
    if (!gid || !entry) return;
    const levelRow = resolveLevelRow(badge?.levelRows, entry?.level);
    pushContributionDetail(details, {
      sectionKey: "badges",
      groupLabel: "Badges",
      label: formatContributionLabel(badge, entry),
      meta: formatContributionLevel(entry, levelRow),
      effects: compileScopedEffectRows(levelRow?.effects, options, badge, levelRow),
    });
  });

  return details;
};

const compileWarplaneBattleInfoFromSections = (warplaneData = {}) => {
  const mergedRows = [
    ...normalizeIdLevelRows(warplaneData?.skills),
    ...normalizeIdLevelRows(warplaneData?.equipSkills),
    ...normalizeIdLevelRows(warplaneData?.equips),
  ];
  const deduped = new Map();
  mergedRows.forEach((entry) => {
    const existing = deduped.get(entry.id);
    if (!existing || entry.level > existing.level) {
      deduped.set(entry.id, entry.level);
    }
  });
  return Array.from(deduped.entries())
    .sort((left, right) => left[0] - right[0])
    .flatMap(([id, level]) => [Math.trunc(id), Math.max(0, Math.floor(asFinite(level, 0)))]);
};

const hasTitanSectionInput = (titansData = {}) => {
  const normalized = normalizeTitansSectionData(titansData);
  return TITAN_SIDE_KEYS.some((sideKey) => {
    const side = normalized?.[sideKey] || normalizeTitanSideData({});
    return side.level > 0
      || side.evolutionStage > 0
      || side.evolutionLevel > 0
      || side.skinGid
      || side.skinStarLevel > 0
      || side.skins.length > 0
      || side.spells.length > 0
      || side.equipPieces.length > 0
      || side.equipSkills.length > 0;
  });
};

const hasWarplaneSectionInput = (warplaneData = {}) => (
  Math.max(0, Math.floor(asFinite(warplaneData?.level, 0))) > 0
  || Math.max(0, Math.floor(asFinite(warplaneData?.advancementStage, 0))) > 0
  || normalizeIdLevelRows(warplaneData?.skills).length > 0
  || normalizeIdLevelRows(warplaneData?.equipSkills).length > 0
  || normalizeIdLevelRows(warplaneData?.equips).length > 0
);

const normalizeOfficerSkillSlots = (rawSlots) => {
  if (!Array.isArray(rawSlots)) return [];
  return [0, 1, 2].map((idx) => {
    const row = rawSlots[idx];
    if (row && typeof row === "object" && !Array.isArray(row)) {
      const skillId = normalizeId(row.skillId);
      const level = Math.max(0, Math.floor(asFinite(row.level, 0)));
      return { skillId, level };
    }
    const skillId = normalizeId(row);
    return { skillId, level: skillId ? 1 : 0 };
  });
};

const normalizeOfficerUnworldlySlots = (rawSlots) => {
  if (!Array.isArray(rawSlots)) return [];
  return [0, 1, 2, 3, 4].map((idx) => {
    const row = rawSlots[idx] && typeof rawSlots[idx] === "object" ? rawSlots[idx] : {};
    return {
      skillId: normalizeId(row.skillId),
      tierId: normalizeId(row.tierId),
      level: Math.max(0, Math.floor(asFinite(row.level, 0))),
    };
  });
};

const parseOfficerRowStars = (row = {}) => decimalStarToStarStage(
  asFinite(row?.stars, 0),
  normalizeId(row?.officerId),
);

const compileOfficerRowsToParity = (rowsRaw = [], options = {}) => {
  if (!Array.isArray(rowsRaw)) {
    return { rows: [], pairArray: [] };
  }
  const combatContext = normalizeCombatContext(options?.combatContext);
  const effectMap = new Map();
  const summaryRows = [];
  const roundEffectValue = (value) => {
    const numeric = asFinite(value, 0);
    return Math.round(numeric * 1000000) / 1000000;
  };

  const addEffect = (gid, value) => {
    const effectGid = normalizeId(gid);
    if (!effectGid) return;
    const numeric = roundEffectValue(value);
    if (numeric === 0) return;
    const existing = roundEffectValue(effectMap.get(effectGid));
    effectMap.set(effectGid, roundEffectValue(existing + numeric));
  };

  rowsRaw.forEach((row, rowIndex) => {
    const appointed = Boolean(row?.appointed);
    const officerId = normalizeId(row?.officerId);
    if (!officerId) return;
    const officer = getOfficerCatalogEntryById(officerId);
    if (!officer) return;

    const level = Math.max(0, Math.floor(asFinite(row?.level, 0)));
    const starInfo = parseOfficerRowStars(row);
    const officerSummary = {
      officerId,
      officerName: officer.label,
      mastery: appointed ? getOfficerMasteryValue(officerId, level, starInfo.stars) : 0,
      effects: [],
    };

    const activeSkillId = normalizeId(row?.activeSkill?.skillId);
    const activeSkillLevel = Math.floor(asFinite(row?.activeSkill?.level, -1));
    if (activeSkillId && activeSkillLevel >= 0) {
      const activePreview = computeOfficerSkillPreview({
        officerId,
        skillId: activeSkillId,
        skillLevel: activeSkillLevel + 1,
        officerLevel: level,
        decimalStars: starInfo.stars,
        applyMastery: false,
      });
      if (activePreview && Array.isArray(activePreview.rows)) {
        activePreview.rows.forEach((effectRow) => {
          const techValue = roundEffectValue(getCombatScopedValue(effectRow.techValue, combatContext, activePreview));
          if (techValue === 0) return;
          addEffect(effectRow.effectId, techValue);
          officerSummary.effects.push({
            source: "active",
            skillId: activePreview.skillId,
            skillName: activePreview.label,
            effectId: effectRow.effectId,
            value: techValue,
            displayValue: effectRow.displayValue,
          });
        });
      }
    }

    if (!appointed) {
      if (officerSummary.effects.length) {
        summaryRows.push({
          ...officerSummary,
          rowIndex,
        });
      }
      return;
    }

    const mastery = getOfficerMasteryValue(officerId, level, starInfo.stars);
    officerSummary.mastery = mastery;
    const skillSlots = normalizeOfficerSkillSlots(row?.skills);
    const unworldlySlots = normalizeOfficerUnworldlySlots(row?.unworldly);

    const basePreview = officer?.baseSkillId
      ? computeOfficerSkillPreview({
        officerId,
        skillId: officer.baseSkillId,
        skillLevel: 1,
        officerLevel: level,
        decimalStars: starInfo.stars,
      })
      : null;
    if (basePreview && Array.isArray(basePreview.rows)) {
      basePreview.rows.forEach((effectRow) => {
        const techValue = roundEffectValue(getCombatScopedValue(effectRow.techValue, combatContext, basePreview));
        if (techValue === 0) return;
        addEffect(effectRow.effectId, techValue);
        officerSummary.effects.push({
          source: "base",
          skillId: basePreview.skillId,
          skillName: basePreview.label,
          effectId: effectRow.effectId,
          value: techValue,
          displayValue: effectRow.displayValue,
        });
      });
    }

    skillSlots.forEach((skillSlot, slotIndex) => {
      if (!skillSlot?.skillId) return;
      if (!isOfficerSkillSlotUnlocked(starInfo.star, slotIndex)) return;
      const preview = computeOfficerSkillPreview({
        officerId,
        skillId: skillSlot.skillId,
        skillLevel: Math.max(1, Math.floor(asFinite(skillSlot.level, 0)) + 1),
        officerLevel: level,
        decimalStars: starInfo.stars,
      });
      if (!preview || !Array.isArray(preview.rows)) return;
      preview.rows.forEach((effectRow) => {
        const techValue = roundEffectValue(getCombatScopedValue(effectRow.techValue, combatContext, preview));
        if (techValue === 0) return;
        addEffect(effectRow.effectId, techValue);
        officerSummary.effects.push({
          source: `skill_${slotIndex + 1}`,
          skillId: preview.skillId,
          skillName: preview.label,
          effectId: effectRow.effectId,
          value: techValue,
          displayValue: effectRow.displayValue,
        });
      });
    });

    const breakthroughLevel = Math.floor(asFinite(row?.breakthroughLevel, -1));
    if (officer?.breakthroughSkillId && breakthroughLevel >= 0 && activeSkillId !== normalizeId(officer.breakthroughSkillId)) {
      const preview = computeOfficerSkillPreview({
        officerId,
        skillId: officer.breakthroughSkillId,
        skillLevel: breakthroughLevel + 1,
        officerLevel: level,
        decimalStars: starInfo.stars,
        applyMastery: false,
      });
      if (preview && Array.isArray(preview.rows)) {
        preview.rows.forEach((effectRow) => {
          const techValue = roundEffectValue(getCombatScopedValue(effectRow.techValue, combatContext, preview));
          if (techValue === 0) return;
          addEffect(effectRow.effectId, techValue);
          officerSummary.effects.push({
            source: "breakthrough",
            skillId: preview.skillId,
            skillName: preview.label,
            effectId: effectRow.effectId,
            value: techValue,
            displayValue: effectRow.displayValue,
          });
        });
      }
    }

    unworldlySlots.forEach((slot, slotIndex) => {
      if (!slot?.skillId || slot.level <= 0) return;
      const resolved = resolveUnworldlyTierBySkill(officerId, slot.skillId, slot.tierId);
      if (!resolved?.tier) return;
      const levelIndex = Math.max(0, Math.min((resolved.tier.effectValues || []).length - 1, slot.level - 1));
      const rawValue = asFinite(resolved.tier.effectValues?.[levelIndex], 0);
      const techValue = roundEffectValue(getCombatScopedValue(
        rawValue,
        combatContext,
        resolved.option,
        resolved.tier,
      ));
      if (techValue === 0) return;
      addEffect(slot.skillId, techValue);
      officerSummary.effects.push({
        source: `unworldly_${slotIndex + 1}`,
        skillId: slot.skillId,
        skillName: resolved.option?.label || `Skill ${slot.skillId}`,
        effectId: slot.skillId,
        value: techValue,
        displayValue: formatParityEffectDisplayValue(techValue, slot.skillId),
        tierId: resolved.tier?.tierId || null,
        tierRank: resolved.tier?.tierRank || null,
      });
    });

    const equipmentEffects = getOfficerEquipmentEffectContributions(officerId, row?.equipment, starInfo.stars);
    if (Array.isArray(equipmentEffects)) {
      equipmentEffects.forEach((entry) => {
        const effectId = Number(entry?.effectId || 0);
        const value = Math.round(asFinite(entry?.value, 0));
        if (effectId <= 0 || value === 0) return;
        addEffect(effectId, value);
        officerSummary.effects.push({
          source: String(entry?.source || "equipment"),
          skillId: null,
          skillName: String(entry?.equipLabel || "Officer Equipment"),
          effectId,
          value,
          displayValue: `${value}`,
          slotIndex: Number(entry?.slotIndex || 0),
          equipId: Number(entry?.equipId || 0),
          masteryId: Number(entry?.masteryId || 0) || null,
          masteryLevel: Number(entry?.masteryLevel || 0) || null,
        });
      });
    }

    const rawSkinLevel = row?.skinLevel == null || row.skinLevel === "" ? NaN : Number(row.skinLevel);
    const skinLevel = Number.isFinite(rawSkinLevel) ? Math.floor(rawSkinLevel) : -1;
    const skinOption = Array.isArray(officer?.skins) ? officer.skins[0] : null;
    const skinRankRows = Array.isArray(skinOption?.rankRows) ? skinOption.rankRows : [];
    if (skinOption && skinRankRows.length && skinLevel >= 0) {
      const selectedSkinRow = skinRankRows.find((entry) => Math.floor(asFinite(entry?.rank, 0)) === skinLevel)
        || [...skinRankRows].reverse().find((entry) => Math.floor(asFinite(entry?.rank, 0)) <= skinLevel);
      (Array.isArray(selectedSkinRow?.primaryEffects) ? selectedSkinRow.primaryEffects : []).forEach((effect) => {
        const effectId = normalizeId(effect?.effectId);
        const value = Math.round(getCombatScopedValue(effect?.value, combatContext, skinOption, selectedSkinRow, effect));
        if (!effectId || value === 0) return;
        addEffect(effectId, value);
        officerSummary.effects.push({
          source: "skin",
          skillId: null,
          skillName: String(skinOption?.label || "Officer Skin"),
          effectId,
          value,
          displayValue: `${value}`,
        });
      });
      skinRankRows
        .filter((entry) => Math.floor(asFinite(entry?.rank, 0)) <= skinLevel)
        .forEach((skinRow) => {
          (Array.isArray(skinRow?.milestoneEffects) ? skinRow.milestoneEffects : []).forEach((effect) => {
            const effectId = normalizeId(effect?.effectId);
            const value = Math.round(getCombatScopedValue(effect?.value, combatContext, skinOption, skinRow, effect));
            if (!effectId || value === 0) return;
            addEffect(effectId, value);
            officerSummary.effects.push({
              source: "skin_milestone",
              skillId: null,
              skillName: String(skinOption?.label || "Officer Skin"),
              effectId,
              value,
              displayValue: `${value}`,
            });
          });
        });
    }

    if (officerSummary.effects.length) {
      summaryRows.push({
        ...officerSummary,
        rowIndex,
      });
    }
  });

  const pairArray = Array.from(effectMap.entries())
    .map(([id, level]) => ({ id: Math.trunc(id), level: roundEffectValue(level) }))
    .filter((entry) => entry.id > 0 && entry.level !== 0)
    .sort((left, right) => left.id - right.id)
    .flatMap((entry) => [entry.id, entry.level]);

  return {
    rows: summaryRows,
    pairArray,
  };
};

export const applySectionDerivedParityFields = (rawParityProfile = {}, rawSections = {}, options = {}) => {
  const normalized = normalizeBaseParityProfile(rawParityProfile);
  const compileOptions = {
    combatContext: normalizeCombatContext(options?.combatContext),
  };
  const sections = rawSections && typeof rawSections === "object" ? rawSections : {};
  const officersData = sections?.officers?.data && typeof sections.officers.data === "object"
    ? sections.officers.data
    : {};
  const titansData = sections?.titans?.data && typeof sections.titans.data === "object"
    ? sections.titans.data
    : {};
  const warplaneData = sections?.warplane?.data && typeof sections.warplane.data === "object"
    ? sections.warplane.data
    : {};
  const badgesData = sections?.badges?.data && typeof sections.badges.data === "object"
    ? sections.badges.data
    : {};

  if (hasTitanSectionInput(titansData)) {
    normalized.titan_b_i = compileTitanBattleInfoFromSections(titansData);
    const titanPassiveRows = compileTitanPassiveEffectRows(titansData);
    if (titanPassiveRows.length > 0) {
      const baseRows = pairArrayToIdLevelRows(normalized.ba_tech);
      normalized.ba_tech = sumIdLevelRowsToPairArray([...baseRows, ...titanPassiveRows]);
    }
  }
  if (hasWarplaneSectionInput(warplaneData)) {
    normalized.fighter_b_i = compileWarplaneBattleInfoFromSections(warplaneData);
  }
  const badgePassiveRows = compileBadgePassiveEffectRows(badgesData);
  if (badgePassiveRows.length > 0) {
    const baseRows = pairArrayToIdLevelRows(normalized.ba_tech);
    normalized.ba_tech = sumIdLevelRowsToPairArray([...baseRows, ...badgePassiveRows]);
  }

  const sectionEffectRows = compileSectionCatalogEffectRows(sections, compileOptions);
  if (sectionEffectRows.length > 0) {
    const baseRows = pairArrayToIdLevelRows(normalized.ba_tech);
    normalized.ba_tech = sumIdLevelRowsToPairArray([...baseRows, ...sectionEffectRows]);
  }

  const officerDerived = compileOfficerRowsToParity(officersData?.rows, compileOptions);
  if (Array.isArray(officerDerived?.pairArray) && officerDerived.pairArray.length > 0) {
    const baseRows = pairArrayToIdLevelRows(normalized.ba_h_tech);
    const derivedRows = pairArrayToIdLevelRows(officerDerived.pairArray);
    normalized.ba_h_tech = sumIdLevelRowsToPairArray([...baseRows, ...derivedRows]);
  }
  normalized.officerEffects = Array.isArray(officerDerived?.rows) ? officerDerived.rows : [];

  PARITY_ARRAY_FIELDS.forEach((fieldKey) => {
    if (fieldKey === "titan_b_i" || fieldKey === "fighter_b_i") return;
    const derivedRows = compileDerivedRowsForParityField(fieldKey, sections, compileOptions);
    if (!derivedRows.length) return;
    const baseRows = pairArrayToIdLevelRows(normalized[fieldKey]);
    normalized[fieldKey] = sumIdLevelRowsToPairArray([...baseRows, ...derivedRows]);
  });

  return normalized;
};

export const buildParityCommanderFromProfile = (profile = null, options = {}) => {
  if (!profile || typeof profile !== "object") {
    return normalizeBaseParityProfile(DEFAULT_COMMANDER_PARITY_PROFILE);
  }
  return applySectionDerivedParityFields(
    profile?.parityProfile && typeof profile.parityProfile === "object"
      ? profile.parityProfile
      : DEFAULT_COMMANDER_PARITY_PROFILE,
    profile?.sections && typeof profile.sections === "object"
      ? profile.sections
      : {},
    options,
  );
};
